import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "react-toastify";
import classes from "./LandingPage2.module.css";
import { useDispatch, useSelector } from "react-redux";
import { useTranslation } from "react-i18next";
import i18n from "../../locales/i18n";
import { setLanguage } from "../../store/slices/languageSlice";

/* ─── Static data ─── */
const TICKER_ITEMS = [
    { emoji: "🟢", label: "LIVE", text: "Predict Goal Scorers" },
    { emoji: "⏱", label: null, text: "First Goal Time" },
    { emoji: "🔔", label: null, text: "Halftime Result" },
    { emoji: "🏆", label: null, text: "Match Winner Prediction" },
];

const TICKER_ITEMS_SW = [
    { emoji: "🟢", label: "MUBASHARA", text: "Tabiri Wafungaji wa Magoli" },
    { emoji: "⏱", label: null, text: "Muda wa Goli la Kwanza" },
    { emoji: "🔔", label: null, text: "Matokeo ya Mapumziko" },
    { emoji: "🏆", label: null, text: "Utabiri wa Mshindi wa Mechi" },
];

const PREDICT_CARDS = [
    {
        icon: "⚽",
        title: "Goal Scorer",
        desc: "Name the player who will score — and in which minute window. The harder the call, the bigger the reward.",
        badge: "⭐ Up to 500 pts",
        isJackpot: false,
    },
    {
        icon: "⏱",
        title: "First Goal Time",
        desc: "Predict the exact minute the first goal is scored. Get within 5 minutes and you score full points.",
        badge: "⭐ Up to 300 pts",
        isJackpot: false,
    },
    {
        icon: "🔔",
        title: "Halftime Score",
        desc: "Forecast the exact scoreline at the break. Hardest call in the game — biggest data reward on offer.",
        badge: "⭐ Up to 750 pts",
        isJackpot: false,
    },
    {
        icon: "🏆",
        title: "Match Winner",
        desc: "Pick the full-time result — home win, away win, or draw. Classic prediction, reliable points.",
        badge: "⭐ Up to 200 pts",
        isJackpot: false,
    },
];

const PREDICT_CARDS_SW = [
    {
        icon: "⚽",
        title: "Mfungaji wa Goli",
        desc: "Mtaje mchezaji atakayefunga — na katika muda gani wa dakika. Jinsi utabiri unavyokuwa mgumu, ndivyo zawadi inavyokuwa kubwa.",
        badge: "⭐ Hadi pts 500",
        isJackpot: false,
    },
    {
        icon: "⏱",
        title: "Muda wa Goli la Kwanza",
        desc: "Tabiri dakika kamili ambayo goli la kwanza litafungwa. Lenga ndani ya tofauti ya dakika 5 na utapata alama zote.",
        badge: "⭐ Hadi pts 300",
        isJackpot: false,
    },
    {
        icon: "🔔",
        title: "Matokeo ya Mapumziko",
        desc: "Tabiri matokeo kamili wakati wa mapumziko. Utabiri mgumu zaidi kwenye mchezo — unaotoa zawadi kubwa zaidi ya alama.",
        badge: "⭐ Hadi pts 750",
        isJackpot: false,
    },
    {
        icon: "🏆",
        title: "Mshindi wa Mechi",
        desc: "Chagua matokeo ya mwisho wa mchezo — ushindi wa nyumbani, ushindi wa ugenini, au sare. Utabiri wa kawaida, alama za uhakika.",
        badge: "⭐ Hadi pts 200",
        isJackpot: false,
    },
];

const REWARDS = [
    {
        icon: "📱",
        title: "Smartphone",
        desc: "Stand a chance to win a smartphone by finishing in the top of the prediction leaderboard.",
    },
    {
        icon: "👕",
        title: "Jersey",
        desc: "Win a football jersey for correctly predicting the match outcome and top scorers.",
    },
    {
        icon: "💰",
        title: "Airtime Credits",
        desc: "Direct airtime credit to your Vodacom number. Use for calls, SMS, or top up your balance.",
    },
];

const REWARDS_SW = [
    {
        icon: "📱",
        title: "Simu Janja (Smartphone)",
        desc: "Pata nafasi ya kushinda simu janja kwa kuongoza au kuwa miongoni mwa walio juu kwenye msimamo wa utabiri.",
    },
    {
        icon: "👕",
        title: "Jezi",
        desc: "Shinda jezi ya mpira wa miguu kwa kutabiri kwa usahihi matokeo ya mechi na wafungaji bora.",
    },
    {
        icon: "💰",
        title: "Muda wa Maongezi (Salio)",
        desc: "Muda wa maongezi utakaotumwa moja kwa moja kwenye namba yako ya Vodacom. Tumia kwa kupiga simu, SMS, au kuongeza salio lako.",
    },
];

const STEPS = [
    {
        icon: "📱",
        num: "Step 01",
        title: "Subscribe",
        desc: "Enter your Vodacom number. Choose Daily (300 TSH), Weekly (800 TSH) or Monthly (1500 TSH). Billed to your mobile.",
    },
    {
        icon: "⚽",
        num: "Step 02",
        title: "Pick a Match",
        desc: "Browse live & upcoming fixtures. Select a match to start making predictions before kick-off.",
    },
    {
        icon: "🎯",
        num: "Step 03",
        title: "Make Predictions",
        desc: "Forecast goal scorers, match winner, halftime score, first goal time & more key moments.",
    },
    {
        icon: "🏆",
        num: "Step 04",
        title: "Collect Rewards",
        desc: "Win smartphone, jersey, bonus data & airtime for every correct call — delivered.",
    },
];

const STEPS_SW = [
    {
        icon: "📱",
        num: "Hatua ya 01",
        title: "Jiunge (Subscribe)",
        desc: "Ingiza namba yako ya Vodacom. Chagua Kifurushi cha Siku (TSH 300), Wiki (TSH 800) au Mwezi (TSH 1500). Utakatwa salio kwenye simu yako.",
    },
    {
        icon: "⚽",
        num: "Hatua ya 02",
        title: "Chagua Mechi",
        desc: "Angalia mechi zinazoendelea sasa na zinazokuja. Chagua mechi ili uanze kufanya utabiri kabla ya mpira kuanza (kick-off).",
    },
    {
        icon: "🎯",
        num: "Hatua ya 03",
        title: "Fanya Utabiri",
        desc: "Tabiri wafungaji wa magoli, mshindi wa mechi, matokeo ya mapumziko, muda wa goli la kwanza na matukio mengine muhimu.",
    },
    {
        icon: "🏆",
        num: "Hatua ya 04",
        title: "Chukua Zawadi",
        desc: "Shinda simu janja, jezi, bando la intaneti (data) na muda wa maongezi kwa kila utabiri sahihi — utafikishiwa zawadi yako.",
    },
];

/* ─── Component ─── */
const LandingPage2 = () => {
    const [msisdn, setMsisdn] = useState("");
    const [selectedPlan, setSelectedPlan] = useState("daily");
    const [loading, setLoading] = useState(false);
    const [showFixedCta, setShowFixedCta] = useState(false);
    const navigate = useNavigate();

    const [tickerItems,setTickerItems]=useState([]);

    const dispatch = useDispatch();

    const { t } = useTranslation();

    const { selectedLanguage } = useSelector((state) => state.languageSlice);

    useEffect(() => {
        if (selectedLanguage && i18n.language !== selectedLanguage) {
            i18n.changeLanguage(String(selectedLanguage));
        }
    }, [selectedLanguage, i18n]);

    const { userDetails } = useSelector(state => state?.persisted?.userSlice)

    useEffect(() => {
        if (userDetails?.token) navigate("/home")
    }, [userDetails])

    useEffect(() => {
        const handleScroll = () => {
            // Show fixed bar after user scrolls past ~80% of hero (roughly one viewport height)
            setShowFixedCta(window.scrollY > window.innerHeight * 0.8);
        };
        window.addEventListener("scroll", handleScroll, { passive: true });
        return () => window.removeEventListener("scroll", handleScroll);
    }, []);

    const handleSubscribe = async () => {
        if (!msisdn.trim()) {
            toast.error(t("0"));
            return;
        }
        try {
            setLoading(true);
            // Replace with your actual loginApi call:
            // const response = await loginApi(msisdn);
            // dispatch(setUserDetails(response?.data));
            toast.success(t("1"));
            navigate("/home");
        } catch (err) {
            toast.error(
                err?.response?.data?.message ||
                err?.response?.message ||
                t("2")
            );
        } finally {
            setLoading(false);
        }
    };

    useEffect(()=>{
        if(selectedLanguage==0){
            setTickerItems(()=>[...TICKER_ITEMS,...TICKER_ITEMS])
        }
        else{
            setTickerItems(()=>[...TICKER_ITEMS_SW,...TICKER_ITEMS_SW]);
        }
    },[selectedLanguage]);

    /* doubled ticker for seamless loop */
    // const tickerItems = [...TICKER_ITEMS, ...TICKER_ITEMS];

    return (
        <div className={classes.page}>

            <select
                id="lang"
                name="lang"
                className={classes.dropdown}
                value={selectedLanguage}
                onChange={(event) => {
                    dispatch(setLanguage(event.target.value));
                }}
            >
                <option value="0">English</option>
                <option value="1">Swahili</option>
            </select>

            {/* ══ HERO ══ */}
            <section className={classes.hero}>
                <div className={classes.heroBg} />
                <div className={classes.heroGlowTop} />
                <div className={classes.heroPitch} />

                <div className={classes.bg}>

                </div>

                <div className={classes.heroContent}>
                    {/* Vodacom logo */}
                    <div className={classes.vodaLogoWrap}>
                        <div className={classes.vodaLogoIcon}>
                            <div className={classes.vodaIconInner}>
                                <svg viewBox="0 0 36 36" xmlns="http://www.w3.org/2000/svg">
                                    <path
                                        fill="white"
                                        d="M18 2C9.16 2 2 8.27 2 16c0 4.67 2.5 8.8 6.4 11.4L7 34l6.8-3.1C14.5 31.3 16.2 31.5 18 31.5 26.84 31.5 34 25.23 34 17.5 34 9.77 26.84 2 18 2z"
                                    />
                                </svg>
                            </div>
                        </div>
                        <div className={classes.vodaWordmark}>vodacom</div>

                        {/* <img src="/assets/images/logo.png" alt="logo" className={classes.logo_img} /> */}
                    </div>

                    {/* Brought to you by */}
                    <div className={classes.broughtRow}>
                        <span className={classes.broughtLabel}>{t("3")}</span>
                        <div className={classes.rippleBadge}>
                            {/* <div className={classes.rippleDot}>
                                <svg viewBox="0 0 14 14" fill="none">
                                    <circle cx="7" cy="7" r="5" stroke="#e60000" strokeWidth="2" />
                                    <circle cx="7" cy="7" r="2" fill="#e60000" />
                                </svg>
                            </div> */}
                            Door2Future Consultants Pvt Ltd
                        </div>
                    </div>

                    {/* Title */}
                    <h1 className={classes.heroHeading}>
                        Vodacom <span className={classes.accentRed}>Live</span>
                        <br />
                        <span className={classes.accentItalic}>Football Game</span>
                    </h1>

                    <p className={classes.heroSub}>
                        {t("4")} &amp; {t("5")}
                        <br />
                        {t("6")}
                    </p>

                    <button
                        className={classes.btnGetStarted}
                        onClick={() =>
                            navigate(`/subscribe`)

                        }
                    >
                        ⚽&nbsp; {t("7")}
                    </button>
                </div>
            </section>

            {/* ══ TICKER ══ */}
            <div className={classes.tickerBar}>
                <div className={classes.tickerTrack}>
                    {tickerItems.map((item, i) => (
                        <div key={i} className={classes.tickerItem}>
                            <em className={classes.tickerEm}>
                                {item.emoji}
                                {item.label ? ` ${item.label}` : ""}
                            </em>
                            {item.text}
                        </div>
                    ))}
                </div>
            </div>

            {/* ══ PREDICTIONS ══ */}
            <section className={classes.predictSection}>
                <div className={classes.sectionInner}>
                    <div className={classes.sectionTag}>{t("8")}</div>
                    <h2 className={classes.sectionHeading}>
                        {t("9")}
                        <br />
                        <span className={classes.accentRed}>{t("10")}</span>
                    </h2>
                    <p className={classes.sectionDesc}>
                        {t("11")}
                    </p>

                    <div className={classes.predictGrid}>
                        {(selectedLanguage == 0 ? PREDICT_CARDS : PREDICT_CARDS_SW).map((card) => (
                            <div
                                key={card.title}
                                className={`${classes.predictCard} ${card.isJackpot ? classes.predictCardJackpot : ""}`}
                            >
                                <span className={classes.pcIcon}>{card.icon}</span>
                                <div className={classes.pcTitle}>{card.title}</div>
                                <p className={classes.pcDesc}>{card.desc}</p>
                                <span
                                    className={`${classes.pcBadge} ${card.isJackpot ? classes.pcBadgeJackpot : ""}`}
                                >
                                    {card.badge}
                                </span>
                            </div>
                        ))}
                    </div>
                </div>
            </section>

            <div className={classes.sectionDivider} />

            {/* ══ REWARDS ══ */}
            <div className={classes.rewardsStrip}>
                <div className={classes.sectionInner}>
                    <div className={classes.rewardsHeader}>
                        <div className={`${classes.sectionTag} ${classes.sectionTagTeal}`}>
                            {t("12")}
                        </div>
                        <h2 className={`${classes.sectionHeading} ${classes.centeredHeading}`}>
                           {t("13")}
                            <br />
                            <span className={classes.accentTeal}>{t("14")}</span>
                        </h2>
                    </div>
                    <div className={classes.rewardsGrid}>
                        {(selectedLanguage == 0 ? REWARDS : REWARDS_SW).map((r) => (
                            <div key={r.title} className={classes.rewardItem}>
                                <span className={classes.riIcon}>{r.icon}</span>
                                <div className={classes.riTitle}>{r.title}</div>
                                <p className={classes.riSub}>{r.desc}</p>
                            </div>
                        ))}
                    </div>
                </div>
            </div>

            {/* ══ HOW IT WORKS ══ */}
            <section className={classes.howSection}>
                <div className={classes.sectionInner}>
                    <div className={classes.sectionTag}>{t("15")}</div>
                    <h2 className={classes.sectionHeading}>
                        {t("16")}
                        <br />
                        <span className={classes.accentRed}>{t("17")}</span>
                    </h2>

                    <div className={classes.stepsRow}>
                        {(selectedLanguage == 0 ? STEPS : STEPS_SW).map((step) => (
                            <div key={step.num} className={classes.step}>
                                <div className={classes.stepCircle}>{step.icon}</div>
                                <div className={classes.stepNum}>{step.num}</div>
                                <div className={classes.stepTitle}>{step.title}</div>
                                <p className={classes.stepDesc}>{step.desc}</p>
                            </div>
                        ))}
                    </div>
                </div>
            </section>

            {/* <div className={classes.sectionDivider} /> */}

            {/* ══ FOOTER ══ */}
            <footer className={classes.footer}>
                <div className={classes.footerLogo}>
                    ⚽ <span className={classes.accentRed}>Vodacom</span> Live Football Game
                </div>
                <div className={classes.footerCopy}>
                    © {new Date().getFullYear()} Door2Future Consultants Pvt Ltd · All rights
                    reserved
                </div>
                <div className={classes.footerVoda}></div>
            </footer>
            {/* ══ FIXED BOTTOM CTA ══ */}
            <div className={`${classes.fixedCta} ${showFixedCta ? classes.fixedCtaVisible : ""}`}>
                <div className={classes.fixedCtaInner}>
                    <div className={classes.fixedCtaText}>
                        <span className={classes.fixedCtaTitle}>⚽ Vodacom Live Football Game</span>
                        <span className={classes.fixedCtaSubtitle}>{t("18")}</span>
                    </div>
                    <button
                        className={classes.fixedCtaBtn}
                        onClick={() =>
                            navigate(`/subscribe`)
                        }
                    >
                        {t("19")}
                    </button>
                </div>
            </div>
        </div>
    );
};

export default LandingPage2;